import React, { useState, useEffect } from 'react';
import { getDailySummary, getOrders } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TrendingUp, Clock, Banknote, CreditCard, ShoppingBag, RefreshCw, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { isCashPaymentMethod } from '../../../lib/paymentMethods';
import { toast } from 'sonner';

function StatCard({ label, value, sub, icon: Icon, colorClass }) {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function shiftDate(isoDate, delta) {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

function formatHour(h) {
  return `${String(h).padStart(2, '0')}:00`;
}

async function fetchShiftData(date) {
  const [summary, orders] = await Promise.all([
    getDailySummary(date).catch(() => null),
    getOrders('completed', date),
  ]);

  return { summary, orders };
}

export default function AdminReportsShifts() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadForDate() {
      setIsLoading(true);
      try {
        const nextData = await fetchShiftData(date);
        if (!cancelled) {
          setSummary(nextData.summary);
          setOrders(nextData.orders);
        }
      } catch {
        if (!cancelled) {
          toast.error('Fehler beim Laden');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadForDate();
    return () => {
      cancelled = true;
    };
  }, [date]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const nextData = await fetchShiftData(date);
      setSummary(nextData.summary);
      setOrders(nextData.orders);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  // Build hourly breakdown from orders
  const hourlyMap = {};
  orders.forEach(o => {
    if (!o.created_at) return;
    const h = new Date(o.created_at).getHours();
    if (!hourlyMap[h]) hourlyMap[h] = { count: 0, total: 0 };
    hourlyMap[h].count++;
    hourlyMap[h].total += o.total || 0;
  });
  const hourlyData = Object.entries(hourlyMap)
    .map(([h, v]) => ({ hour: parseInt(h), ...v }))
    .sort((a, b) => a.hour - b.hour);

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const cashRevenue  = orders.filter(o => isCashPaymentMethod(o.payment_method)).reduce((s, o) => s + (o.total || 0), 0);
  const cardRevenue  = orders.filter(o => !isCashPaymentMethod(o.payment_method)).reduce((s, o) => s + (o.total || 0), 0);
  const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

  const maxHourly = Math.max(...hourlyData.map(h => h.total), 1);

  return (
    <div className="p-6 space-y-6">
      {/* Header + date navigation */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Schichten</h1>
          <p className="text-slate-500">Tagesabschluss und Umsatzübersicht</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Date picker */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setDate(d => shiftDate(d, -1))}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-44"
        />
        <Button
          variant="outline"
          size="icon"
          onClick={() => setDate(d => shiftDate(d, 1))}
          disabled={date >= new Date().toISOString().slice(0, 10)}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <span className="text-sm text-slate-500 ml-2">
          {new Date(date + 'T00:00:00').toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-[#1a2f38]" />
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Gesamtumsatz"
              value={`€${totalRevenue.toFixed(2)}`}
              icon={TrendingUp}
              colorClass="bg-emerald-50 text-emerald-600"
            />
            <StatCard
              label="Bestellungen"
              value={orders.length}
              sub={`Ø €${avgOrderValue.toFixed(2)} pro Bestellung`}
              icon={ShoppingBag}
              colorClass="bg-blue-50 text-blue-600"
            />
            <StatCard
              label="Bargeld"
              value={`€${cashRevenue.toFixed(2)}`}
              icon={Banknote}
              colorClass="bg-amber-50 text-amber-600"
            />
            <StatCard
              label="Karte"
              value={`€${cardRevenue.toFixed(2)}`}
              icon={CreditCard}
              colorClass="bg-indigo-50 text-indigo-600"
            />
          </div>

          {/* Hourly chart */}
          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-slate-400" />
                <h3 className="font-semibold text-slate-700">Umsatz nach Stunde</h3>
              </div>
              {hourlyData.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Keine Daten für diesen Tag</p>
              ) : (
                <div className="space-y-2">
                  {hourlyData.map(({ hour, count, total }) => (
                    <div key={hour} className="flex items-center gap-3 text-sm">
                      <span className="w-12 text-slate-500 tabular-nums">{formatHour(hour)}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-[#1a2f38] rounded-full transition-all"
                          style={{ width: `${(total / maxHourly) * 100}%` }}
                        />
                      </div>
                      <span className="w-20 text-right tabular-nums font-medium">€{total.toFixed(2)}</span>
                      <span className="w-16 text-right text-slate-400 tabular-nums">{count} Best.</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cash register summary from backend if available */}
          {summary && (
            <Card className="shadow-sm border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-700 mb-3">Kassenabschluss</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ['Eröffnungsbetrag', `€${(summary.opening_amount || 0).toFixed(2)}`],
                    ['Schließbetrag',    `€${(summary.closing_amount || 0).toFixed(2)}`],
                    ['Kassenstand',      `€${(summary.cash_in_drawer || 0).toFixed(2)}`],
                    ['Differenz',        `€${(summary.difference || 0).toFixed(2)}`],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <p className="text-slate-400 text-xs">{label}</p>
                      <p className="font-semibold">{val}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
